// Tennis Intelligence Module — section surfaces (Tennis Sidebar Navigation
// v1.0 + Tennis PRD v1.0 + Market Intelligence v1.1).
//
// Five editorial surfaces in the STRIKZA trading-terminal language with the
// Tennis accent (--sz-tennis). Styling lives in src/styles/tennis.css; this
// file composes layouts from the _kit primitives.
//   signals    → Set Intelligence (TIM) — hero landing
//   tournament → Grand Slam overview + Grand Slam Pass
//   clv        → Closing-Line Value dashboard            (Pro+ / phantom_pro)
//   no-bets    → Disciplined passes (NO_BET report)
//   markets    → Market intelligence reference           (Elite+ / desk_pro)
//
// DOCTRINE (binding):
//   · No fabricated tennis data. Live surfaces show honest, designed
//     pre-open / awaiting states until real TIM outputs flow. Legends, the
//     round ladder and the market list are factual or educational only.
//   · Methodology stays private — no model weights, no exact edge thresholds,
//     no per-market win rates, no agent specialisation.
//
// Tier gates: CLV Track → phantom_pro ("Pro+"); Markets → desk_pro ("Elite+").
// Grand Slam Pass: free = Locked · sika_pro/phantom_pro = Purchase ·
// desk_pro+ = Included.

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../../components/icons/Icon.tsx";
import { useUserTier } from "../../auth/useUserTier.ts";
import { tierMeets, type TierGate } from "../../auth/tier-gates.ts";
import {
  AwaitingPanel,
  Badge,
  Card,
  daysUntil,
  Grid,
  Kicker,
  Legend,
  PilotStrip,
  R1_DATE,
  Stack,
  StatTile,
  StatusPill,
  TennisHeader,
  Timeline,
  UpsellNote,
  type LegendItem,
  type TimelineItem,
} from "./_kit.tsx";
import { productBySlug } from "./products/registry.ts";
import { useTennisProduct, type TennisPick, type UseTennisProductState } from "./products/useTennisProduct.ts";
import { TennisPickList } from "./products/TennisPickList.tsx";

// A few flagship products surfaced on the Signals landing as premium teaser
// cards (same .tn-pcard language as the Dashboard). Links out to each screen.
const FEATURED_PRODUCTS = ["powerball", "full-slate", "court-card", "clay-trap"] as const;

function FeaturedProductCards({ tierCode }: { tierCode: string | null }): JSX.Element {
  return (
    <div className="tn-pboard">
      {FEATURED_PRODUCTS.map((slug) => {
        const p = productBySlug(slug);
        if (!p) return null;
        // Tier chip is an upsell — only show it when the viewer lacks access.
        const showTier = !tierMeets(tierCode, p.tier);
        return (
          <Link key={slug} to={`/exchange/tennis/p/${slug}`} className={`tn-pcard tn-pcard--${p.family}`}>
            <div className="tn-pcard__top">
              <div className="tn-pcard__name">{p.name}</div>
              <Icon name="chev" size={13} className="tn-pcard__go" />
            </div>
            <div className="tn-pcard__tags">
              <Badge tone={p.family === "volume" ? "tennis" : "info"}>{p.family === "volume" ? "Volume" : "Layer"}</Badge>
              {showTier ? <Badge tone="muted">{p.tierLabel}</Badge> : null}
            </div>
            <div className="tn-pcard__teaser">{p.tagline}</div>
          </Link>
        );
      })}
    </div>
  );
}

export type TennisSection = "signals" | "tournament" | "clv" | "no-bets" | "markets";

const HEADER: Record<TennisSection, { eyebrow: string; title: string }> = {
  signals: { eyebrow: "Tennis · Signals", title: "Set Intelligence" },
  tournament: { eyebrow: "Tennis · Tournament", title: "French Open 2026" },
  clv: { eyebrow: "Tennis · CLV Track", title: "Closing-Line Value" },
  "no-bets": { eyebrow: "Tennis · No-Bet Report", title: "Disciplined Passes" },
  markets: { eyebrow: "Tennis · Markets", title: "Market Intelligence" },
};

const R1_COUNTDOWN = (): { value: string; label: string } => {
  const d = daysUntil(R1_DATE);
  return { value: d === 0 ? "Live" : String(d), label: d === 0 ? "Round 1 open" : d === 1 ? "day to Round 1" : "days to Round 1" };
};

// ════════════════════════════════════════════════════════════════════
// Shell
// ════════════════════════════════════════════════════════════════════
function HeaderMeta(): JSX.Element {
  const d = daysUntil(R1_DATE);
  return <StatusPill live={d === 0}>{d === 0 ? "Pilot · live" : "Pilot · pre-tournament"}</StatusPill>;
}

function PageShell({ section, children }: { section: TennisSection; children: React.ReactNode }): JSX.Element {
  const h = HEADER[section];
  return (
    <div className="tn">
      <TennisHeader eyebrow={h.eyebrow} title={h.title} meta={<HeaderMeta />} />
      {children}
    </div>
  );
}

function LockedPanel({ tierLabel, what }: { tierLabel: string; what: string }): JSX.Element {
  return (
    <div className="tn-lock">
      <span className="tn-lock__icon">
        <Icon name="lock" size={22} />
      </span>
      <div className="tn-lock__title">{tierLabel} feature</div>
      <div className="tn-lock__body">{what}</div>
      <Link to="/pricing" className="tn-cta">
        See plans →
      </Link>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// SIGNALS — hero landing
// ════════════════════════════════════════════════════════════════════
const SIGNAL_LEGEND: readonly LegendItem[] = [
  {
    badge: <Badge tone="pos" dot>BET</Badge>,
    title: "Qualified edge",
    body: "The Tennis Fair Price Engine sees a clay-priced gap over the book wide enough to clear our discipline bar. Surfaced with a fair-value context and a confidence band — never a guaranteed outcome.",
  },
  {
    badge: <Badge tone="warn" dot>MONITOR</Badge>,
    title: "Watching",
    body: "Edge is present but it sits below the bet bar, or the line is still moving. Logged and tracked — not a recommendation to stake.",
  },
  {
    badge: <Badge tone="muted" dot>NO_BET</Badge>,
    title: "Disciplined pass",
    body: "No qualifying edge, or a risk flag held the slip. The discipline to pass is the product — every pass is published in the No-Bet Report.",
  },
];

// The six signal tags (Full Draw Coverage v1.0 §6.2) — UI flags translating
// model output into language.
const SIGNAL_TAGS: readonly { tag: string; meaning: string }[] = [
  { tag: "CLAY SPECIALIST", meaning: "Clay level outstrips ranking" },
  { tag: "SLOW START RISK", meaning: "Top seed prone to dropping the first set" },
  { tag: "STEAM MOVE", meaning: "Sharp money moved the line" },
  { tag: "FATIGUE RISK", meaning: "Heavy recent match load" },
  { tag: "FIRST MEETING", meaning: "No clay head-to-head history" },
  { tag: "LOW PROFILE", meaning: "Thin data — graded, not hidden" },
];

// Anatomy of a signal card (§6.2) — shown as a labelled structural preview
// until live cards populate.
const CARD_ZONES: readonly { label: string; demo: string }[] = [
  { label: "Top strip", demo: "Signal badge · market · edge % · confidence" },
  { label: "Match info", demo: "Player A vs Player B — rankings, seed, court, time" },
  { label: "Probability row", demo: "STRIKZA fair % vs Book implied % — the gap (Pro+)" },
  { label: "Signal tags", demo: "Up to 3 inline flags (see legend below)" },
  { label: "Recommended odds", demo: "Target price + one-tap to the book" },
  { label: "No-bet reason", demo: "Plain-language why a pass was a pass" },
];

// The Daily Acca is a pure SELECTOR over the day's full-slate board — exactly
// the card's promise: the top BET signals, one leg per match (the board is
// already fixture-deduped), no READ/LOW-confidence picks, 3–5 legs, minimum
// 3.00 combined. Returns null (honest-empty) when the day can't field one.
function selectDailyAcca(picks: TennisPick[]): { legs: TennisPick[]; combined: number } | null {
  const eligible = picks
    .filter((p) =>
      typeof p.book_odds === "number" && p.book_odds > 1 &&
      p.tier !== "READ" &&
      String(p.confidence ?? "").toLowerCase() !== "low")
    .sort((a, b) => (b.edge_pct ?? -1e6) - (a.edge_pct ?? -1e6));
  const legs: TennisPick[] = [];
  let combined = 1;
  for (const p of eligible) {
    legs.push(p);
    combined *= p.book_odds as number;
    if (legs.length >= 3 && combined >= 3.0) break; // smallest qualifying acca
    if (legs.length >= 5) break;                     // cap at 5 legs
  }
  return legs.length >= 3 && combined >= 3.0 ? { legs, combined } : null;
}

function AccaGhosts({ tag }: { tag: string }): JSX.Element {
  return (
    <div className="tn-acca__legs">
      {Array.from({ length: 3 }).map((_, i) => (
        <div className="tn-acca__leg" key={i}>
          <span className="tn-ghost__bar tn-ghost__bar--w1" />
          <span className="tn-ghost__tag">{tag}</span>
        </div>
      ))}
    </div>
  );
}

function DailyAccaCard({ board }: { board: UseTennisProductState }): JSX.Element {
  const cd = R1_COUNTDOWN();
  const acca = board.found ? selectDailyAcca(board.picks) : null;
  return (
    <section className="tn-acca">
      <div className="tn-acca__head">
        <div>
          <Kicker green>Daily Acca</Kicker>
          <div className="tn-acca__title">STRIKZA Daily Acca · French Open 2026</div>
          <div style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 6, maxWidth: "52ch", lineHeight: 1.55 }}>
            The curated daily product — a disciplined 3–5 leg accumulator built from the day's top BET signals,
            minimum 3.00 combined. One leg per match, no LOW-confidence picks.
          </div>
        </div>
        <div className="tn-acca__odds">
          <div className="tn-acca__odds-v">{acca ? acca.combined.toFixed(2) : "—"}</div>
          <span className="tn-await__count-l">{acca ? "combined odds" : cd.label}</span>
        </div>
      </div>
      {board.loading ? (
        <AccaGhosts tag="Loading…" />
      ) : acca ? (
        <TennisPickList picks={acca.legs} />
      ) : (
        <AccaGhosts tag={board.found ? "No qualifying acca today" : "Opens Sun 24 May"} />
      )}
      <div className="tn-acca__actions">
        <span className="tn-cta tn-cta--ghost">Copy Slip</span>
        <span className="tn-cta tn-cta--ghost">Stake on Stake</span>
      </div>
    </section>
  );
}

function SignalsSection({ tierCode }: { tierCode: string | null }): JSX.Element {
  const fullPipeline = tierMeets(tierCode, "phantom_pro");
  // Layer 1 — the live full-draw board (engine pool via get_tennis_product).
  // Free/Starter tiers see a capped preview; Pro+ sees the whole board.
  const board = useTennisProduct("full-slate");
  const allPicks = board.picks;
  const picks = fullPipeline ? allPicks : allPicks.slice(0, 3);
  const hasBoard = board.found && allPicks.length > 0;
  return (
    <Stack>
      <PilotStrip />

      {/* Layer 3 — the Daily Acca sits at the top (Full Draw Coverage §6.3).
          Selected live from the day's full-slate board. */}
      <DailyAccaCard board={board} />

      {/* The three layers of full coverage (§4). */}
      <div className="tn-layers">
        <div className="tn-layer">
          <span className="tn-layer__num">LAYER 1</span>
          <div className="tn-layer__name">The Full Draw</div>
          <div className="tn-layer__desc">Every match priced — BET, MONITOR and NO_BET shown. Total transparency: see what was rejected, not just what was picked.</div>
        </div>
        <div className="tn-layer">
          <span className="tn-layer__num">LAYER 2</span>
          <div className="tn-layer__name">BET Signals</div>
          <div className="tn-layer__desc">Filtered to qualified edges only. Build your own slip from the actionable intelligence — typically 8–15 on a peak day.</div>
        </div>
        <div className="tn-layer">
          <span className="tn-layer__num">LAYER 3</span>
          <div className="tn-layer__name">The Daily Acca</div>
          <div className="tn-layer__desc">STRIKZA's curated 3–5 leg pick from the top BET signals. The finished intelligence — most users start and end here.</div>
        </div>
      </div>

      {!fullPipeline && (
        <UpsellNote cta="Upgrade">
          You see <strong>&nbsp;3 signals per day</strong>&nbsp;— the full draw + full pipeline is part of Pro and above.
        </UpsellNote>
      )}

      {/* Bet products live on their own screens — premium teaser cards. */}
      <Card eyebrow="Bet products" title="Suggested accas & picks">
        <p className="tn-card__lead">
          Every product — Powerball, Full Slate, Court Card, the intelligence layers and more — has its own screen with its suggested acca or picks to stake.
        </p>
        <FeaturedProductCards tierCode={tierCode} />
        <div style={{ marginTop: 14 }}>
          <Link to="/exchange/tennis/dashboard" className="tn-cta">Open Tennis Dashboard →</Link>
        </div>
      </Card>

      {/* Layer 1 — the live full-draw feed (wired to the full-slate board;
          honest-empty until the engine publishes). */}
      <Card eyebrow="Layer 1 · The Full Draw" title="Every match, priced" accent>
        {board.loading ? (
          <AwaitingPanel
            eyebrow="Reading the pool"
            title="Loading the live board…"
            body="Pulling every priced match from the intelligence pool."
            ghosts={3}
          />
        ) : hasBoard ? (
          <>
            <p className="tn-card__lead">
              Every singles match we&rsquo;ve priced — tap any row for the Fair Price drawer (our model read vs the book across every market).{" "}
              {allPicks.length} priced{board.pickDate ? ` · ${board.pickDate}` : ""}.
            </p>
            <TennisPickList picks={picks} />
            {!fullPipeline && allPicks.length > picks.length ? (
              <UpsellNote cta="Upgrade">
                Showing <strong>&nbsp;{picks.length} of {allPicks.length}</strong>&nbsp;priced matches — the full draw is part of Pro and above.
              </UpsellNote>
            ) : null}
          </>
        ) : (
          <AwaitingPanel
            eyebrow="Awaiting Round 1"
            title="The full draw populates when Roland Garros opens."
            body="Every singles match across the ATP and WTA main draw will be priced — not a pre-selected few. Until the draw is live this stays honestly empty: no mock signals, no fabricated edges. The matches nobody is watching are where the edge usually lives."
            countdown={R1_COUNTDOWN()}
            ghosts={3}
            ghostTag="Opens Sun 24 May"
          />
        )}
      </Card>

      {/* Signal card anatomy (§6.2). */}
      <Card eyebrow="Anatomy of a signal card" title="What each match card shows">
        <div className="tn-anatomy">
          {CARD_ZONES.map((z) => (
            <div className="tn-zone" key={z.label}>
              <span className="tn-zone__label">{z.label}</span>
              <div className="tn-zone__demo">{z.demo}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Signal tags legend (§6.2). */}
      <Card eyebrow="Signal flags" title="The tags you'll see on a card">
        <Legend items={SIGNAL_TAGS.map((t) => ({ badge: <span className="tn-tag">{t.tag}</span>, title: t.meaning, body: "" }))} />
      </Card>

      <Card eyebrow="How to read a signal" title="Signal taxonomy">
        <Legend items={SIGNAL_LEGEND} />
      </Card>
    </Stack>
  );
}

// ════════════════════════════════════════════════════════════════════
// TOURNAMENT — overview + Grand Slam Pass
// ════════════════════════════════════════════════════════════════════
const ROUNDS: readonly TimelineItem[] = [
  { round: "Round 1", when: "from Sun 24 May", key: true },
  { round: "Round 2", when: "scheduled" },
  { round: "Round 3", when: "scheduled" },
  { round: "Round 4", when: "scheduled" },
  { round: "Quarter-finals", when: "scheduled" },
  { round: "Semi-finals", when: "scheduled" },
  { round: "Final", when: "Sun 7 Jun", key: true },
];

function GrandSlamPass({ tierCode }: { tierCode: string | null }): JSX.Element {
  const included = tierMeets(tierCode, "desk_pro");
  const purchasable = !included && tierMeets(tierCode, "sika_pro");
  return (
    <Card accent>
      <div className="tn-pass">
        <div className="tn-pass__copy">
          <Kicker green>Grand Slam Pass</Kicker>
          <div className="tn-pass__title">Full tournament access · French Open 2026</div>
          <div className="tn-pass__sub">
            Every round, every qualified set-betting signal across the ATP and WTA main draw for the duration of
            Roland Garros.
          </div>
        </div>
        {included ? (
          <Badge tone="pos" dot>Included</Badge>
        ) : purchasable ? (
          <Link to="/pricing" className="tn-cta">
            Purchase →
          </Link>
        ) : (
          <span className="tn-pass__locked">
            <Icon name="lock" size={14} /> Locked
          </span>
        )}
      </div>
    </Card>
  );
}

function TournamentSection({ tierCode }: { tierCode: string | null }): JSX.Element {
  return (
    <Stack>
      <PilotStrip />
      <GrandSlamPass tierCode={tierCode} />

      <Grid>
        <StatTile label="Event" value="Roland Garros" sub="Paris · clay" tone="green" />
        <StatTile label="Draws" value="ATP + WTA" sub="Singles main draw" />
        <StatTile label="Coverage" value="R1 → Final" sub="Qualifying excluded" />
      </Grid>

      <Card eyebrow="Schedule" title="Round ladder">
        <Timeline items={ROUNDS} />
      </Card>

      <Card eyebrow="Draw & results" title="Live tournament context">
        <AwaitingPanel
          eyebrow="Awaiting draw"
          title="The draw, schedule and results populate when the tournament opens."
          body="Once Round 1 begins, this section carries the live draw, match results and round-by-round context. No bracket is shown before the official draw — we don't fabricate fixtures."
          countdown={R1_COUNTDOWN()}
        />
      </Card>
    </Stack>
  );
}

// ════════════════════════════════════════════════════════════════════
// CLV TRACK — Pro+ (phantom_pro)
// ════════════════════════════════════════════════════════════════════
const CLV_LEGEND: readonly LegendItem[] = [
  { badge: <Badge tone="pos">{">"} +5%</Badge>, title: "Strong edge", body: "Beat the closing line by a wide margin — the cleanest confirmation a signal found genuine value before the market corrected." },
  { badge: <Badge tone="tennis">+2% to +5%</Badge>, title: "Edge present", body: "Positive closing-line value — the bet was placed at a better price than the market settled on. The standard healthy result." },
  { badge: <Badge tone="muted">0% to +2%</Badge>, title: "Marginal", body: "Roughly flat to close. Logged, but never used to justify stake sizing." },
  { badge: <Badge tone="neg">{"<"} 0%</Badge>, title: "Negative CLV", body: "Placed worse than the market closed — an execution miss. Triggers a mandatory slip autopsy, surfaced in the No-Bet Report." },
];

function ClvSection(): JSX.Element {
  return (
    <Stack>
      <PilotStrip />

      <Grid>
        <StatTile label="Avg CLV" value="—" sub="Closing-line value · awaiting bets" tone="muted" />
        <StatTile label="Brier score" value="—" sub="Calibration · lower is better" tone="muted" />
        <StatTile label="Signals scored" value="0" sub="Settled this tournament" tone="muted" />
      </Grid>

      <Card
        eyebrow="Why CLV"
        title="Closing-Line Value is the North Star"
        accent
        lead="We don't measure ourselves by wins or streaks — variance lies. We measure whether each signal beat the market's closing price (CLV) and how well-calibrated the probabilities were (Brier). Over a meaningful sample, positive CLV is the honest evidence of edge."
      />

      <Card eyebrow="Reading the dashboard" title="CLV bands">
        <Legend items={CLV_LEGEND} />
      </Card>

      <Card eyebrow="Running dashboard" title="Tournament CLV & calibration">
        <AwaitingPanel
          eyebrow="Awaiting settled signals"
          title="The CLV dashboard fills as signals settle, round by round."
          body="Average CLV, Brier score and the edge distribution populate from real placed-and-settled signals only. Before the first settled match these stay empty — we never seed the dashboard with illustrative numbers."
          countdown={R1_COUNTDOWN()}
        />
      </Card>
    </Stack>
  );
}

// ════════════════════════════════════════════════════════════════════
// NO-BET REPORT — disciplined passes
// ════════════════════════════════════════════════════════════════════
const NOBET_LEGEND: readonly LegendItem[] = [
  { badge: <Badge tone="muted">INSUFFICIENT EDGE</Badge>, title: "Below the bar", body: "The fair-value gap didn't clear our discipline threshold. The most common — and most important — pass." },
  { badge: <Badge tone="warn">EDGE BELOW THRESHOLD</Badge>, title: "Watch, don't stake", body: "Some edge, but not enough to bet. Logged as a MONITOR rather than acted on." },
  { badge: <Badge tone="neg">ANOMALY HOLD</Badge>, title: "Risk flag held it", body: "An anomaly — a suspicious line move, a data gap, or a volatility flag — overrode the edge. We pass on principle." },
  { badge: <Badge tone="muted">NO PRICE</Badge>, title: "No reliable price", body: "No trustworthy market price was available to anchor a fair value. Without a clean anchor, there's no honest bet." },
];

function NoBetsSection(): JSX.Element {
  return (
    <Stack>
      <PilotStrip />

      <Card
        eyebrow="Discipline"
        title="The passes are the product"
        accent
        lead="Most matches are not bets. Every signal the pipeline declines is published here with its reason code — so the discipline is auditable, not invisible. A NO_BET is a decision, not the absence of one."
      />

      <Card eyebrow="Reason codes" title="Why a signal becomes a pass">
        <Legend items={NOBET_LEGEND} />
      </Card>

      <Card eyebrow="Published after each round" title="No-Bet log">
        <AwaitingPanel
          eyebrow="Awaiting Round 1"
          title="Passes publish here as each round completes."
          body="After every round, all NO_BET signals are listed with their reason codes. Nothing appears before Round 1 plays — the log reflects real pipeline decisions, never filler."
          countdown={R1_COUNTDOWN()}
          ghosts={2}
          ghostTag="After Round 1"
        />
      </Card>
    </Stack>
  );
}

// ════════════════════════════════════════════════════════════════════
// MARKETS — Elite+ (desk_pro)
// ════════════════════════════════════════════════════════════════════
// Reference surface. Per the binding privacy rule we publish market NAMES,
// activation status and liquidity — but NOT model weights, exact edge
// thresholds, or which markets the agents specialise in.
type MktStatus = "live" | "standby" | "roadmap";
const MARKETS: readonly { name: string; tier: string; status: MktStatus; note: string }[] = [
  { name: "X to Win a Set", tier: "Primary · P1", status: "live", note: "The anchor market. Win at least one set — clay amplifies the underdog set chances books underprice." },
  { name: "Games Handicap", tier: "Primary · P2", status: "live", note: "Spread on total games. Clay's longer, more competitive games make the handicap line exploitable." },
  { name: "Total Games O/U", tier: "Primary · P3", status: "live", note: "Over/under on total games — deep liquidity and a clean Pinnacle close for CLV." },
  { name: "Total Sets O/U", tier: "Primary · P4", status: "live", note: "Over/under on total sets. Strong model fit to the clay set-competition picture." },
  { name: "First Set Winner", tier: "Secondary · P5", status: "standby", note: "Activates after the Round 1 CLV review confirms a reliable close." },
  { name: "Correct Set Score", tier: "Secondary · P6", status: "standby", note: "Higher variance — activated selectively after Round 1." },
  { name: "Match Tiebreak", tier: "Roadmap", status: "roadmap", note: "Not active for this pilot — earliest consideration is a future Grand Slam." },
];

function MktStatusBadge({ status }: { status: MktStatus }): JSX.Element {
  if (status === "live") return <Badge tone="pos" dot>Live · R1</Badge>;
  if (status === "standby") return <Badge tone="warn">Standby · R2</Badge>;
  return <Badge tone="muted">Roadmap</Badge>;
}

function MarketsSection(): JSX.Element {
  return (
    <Stack>
      <PilotStrip />

      <Card
        eyebrow="Pilot markets"
        title="What's active for Roland Garros"
        accent
        lead="Four primary markets are live from Round 1. Two secondary markets are on standby pending the Round 1 CLV review. Every market here has a reliable Pinnacle closing line — no anchor, no Primary status."
      />

      <Card eyebrow="Reference" title="Market scorecard">
        <div className="tn-table">
          {MARKETS.map((m) => (
            <div className="tn-mkt" key={m.name}>
              <div>
                <div className="tn-mkt__name">
                  <span className="tn-mkt__title">{m.name}</span>
                  <span className={m.tier.startsWith("Primary") ? "tn-mkt__tier tn-mkt__tier--primary" : "tn-mkt__tier"}>
                    {m.tier}
                  </span>
                </div>
                <div className="tn-mkt__note">{m.note}</div>
              </div>
              <div className="tn-mkt__status">
                <MktStatusBadge status={m.status} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card eyebrow="Activation" title="Round-by-round status">
        <AwaitingPanel
          eyebrow="Awaiting Round 1 review"
          title="Secondary markets activate after the Round 1 CLV review."
          body="Activation status updates here once Round 1 settles and the CLV review confirms each secondary market has a dependable closing line. Until then the four primary markets carry the pilot."
          countdown={R1_COUNTDOWN()}
        />
      </Card>
    </Stack>
  );
}

// ════════════════════════════════════════════════════════════════════
// Router
// ════════════════════════════════════════════════════════════════════
export function TennisPage({ section }: { section: TennisSection }): JSX.Element {
  const { tierCode } = useUserTier();
  const code = tierCode ?? null;

  // Page-level tier gates (defence in depth — the sidebar also locks these).
  const gate: TierGate | null = section === "clv" ? "phantom_pro" : section === "markets" ? "desk_pro" : null;
  if (gate && !tierMeets(code, gate)) {
    return (
      <PageShell section={section}>
        <Stack>
          <PilotStrip />
        </Stack>
        <LockedPanel
          tierLabel={gate === "desk_pro" ? "Elite" : "Pro"}
          what={
            section === "clv"
              ? "The CLV Track — running closing-line value and calibration for the tournament — is part of the Pro tier and above."
              : "Market Intelligence — the active markets, activation schedule and edge reference — is part of the Elite tier and above."
          }
        />
      </PageShell>
    );
  }

  return (
    <PageShell section={section}>
      {section === "signals" && <SignalsSection tierCode={code} />}
      {section === "tournament" && <TournamentSection tierCode={code} />}
      {section === "clv" && <ClvSection />}
      {section === "no-bets" && <NoBetsSection />}
      {section === "markets" && <MarketsSection />}
    </PageShell>
  );
}
